import { provideStore } from '@ngxs/store';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { environment } from '@environments/environment';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideRouter, Router } from '@angular/router';
import {
  CapturumHttpCodeInterceptor,
  ErrorMessageInterceptor,
  PermissionGuard,
  provideCapturumComplete,
  TranslationLoader,
} from '@capturum/complete';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideCapturumFormly } from '@capturum/formly';
import { provideCapturumUI } from '@capturum/ui/config';
import { providePrimeNG } from 'primeng/config';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { provideCapturumLogin } from '@capturum/login';
import { passwordValidator } from '@shared/validators/password.validator';
import { provideCapturumShared } from '@capturum/shared';
import { BaseDataService } from '@core/services/base-data.service';
import { ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { SentryErrorHandler } from '@core/error-handlers/SentryErrorHandler';
import * as Sentry from '@sentry/angular';
import { provideCapturumAuth } from '@capturum/auth';
import { provideCapturumApi } from '@capturum/api';
import { API_DATE_BLACKLIST, API_DATE_PROPERTIES, DatesFormats } from '@core/configs/date-config';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  provideTranslateService,
  TranslateLoader,
} from '@ngx-translate/core';
import { DentalCompleteResolver } from '@core/resolvers/dental-complete.resolver';
import { DentalBaseDataResolver } from '@core/resolvers/dental-base-data.resolver';
import { OrderResolver } from '@core/resolvers/order.resolver';
import { AttributesBaseDataResolver } from '@core/resolvers/attributes-base-data.resolver';
import { AttributesTranslationResolver } from '@core/resolvers/attributes-translation.resolver';
import { FormBuilderResolver } from '@features/client/form-builder/resolvers/form-builder.resolver';
import { FormBuilderApiService } from '@features/client/form-builder/services/form-builder-api.service';
import { FormBuilderConfigService } from '@features/client/form-builder/form-builder.config';
import { dynamicForms } from '@features/client/order-workflow/configs/dynamic-forms.config';
import { urlPlaceholders } from '@features/client/order-workflow/configs/url-placeholders.config';
import { provideWorkflowManager } from '@features/client/workflow-manager/provide-workflow-manager';
import { NgxPermissionsModule } from 'ngx-permissions';
import { provideCapturumBuildersCore } from '@capturum/builders/core';
import { buildersCoreConfig } from '../configs/builders-core.config';
import { appRoutes } from './app.routes';
import { appPreset } from './app.preset';
import { LoginService } from '@features/auth/services/login.service';
import { FormBuilderModel } from '@features/client/form-builder/models/form-builder.model';
import { TranslateDentalPipe } from '@shared/pipes/translate-dental.pipe';
import { OrderWorkflowState } from '@features/client/order-workflow/state/order-workflow.state';
import { WorkflowManagerState } from '@features/client/workflow-manager/state/workflow-manager.state';
import { FormBuilderState } from '@features/client/form-builder/state/form-builder.state';
import { UsersState } from '@features/client/users/state/users.state';
import { provideFormBuilder } from '@features/client/form-builder/provide-form-builder';
import { HttpCodeInterceptor } from '@core/interceptors/code.interceptor';
import { OrderInterceptor } from '@core/interceptors/order.interceptor';

export class CustomMissingTranslationHandler implements MissingTranslationHandler {
  public handle(params: MissingTranslationHandlerParams): string {
    return `[[ ${params.key} ]]`;
  }
}

function createAuthErrorHandler(targetUrl: string) {
  let redirectInProgress = false;

  return (): void => {
    if (redirectInProgress) {
      return;
    }

    redirectInProgress = true;

    queueMicrotask(() => {
      if (!window.location.pathname.startsWith(targetUrl)) {
        window.location.assign(targetUrl);
      }
    });
  };
}

const onAuthError = createAuthErrorHandler(`auth/login?redirectUrl=${window.location.pathname}`);

export const appConfig = {
  providers: [
    // Capturum Complete
    provideCapturumComplete({
      indexedDbModels: [FormBuilderModel],
      databaseName: 'capturum-complete-db',
      baseDataService: BaseDataService,
      version: 14,
    }),
    // Router
    provideRouter(appRoutes),
    // HTTP client with class-based interceptor support
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    // Animations
    provideAnimationsAsync(),
    // Permissions
    importProvidersFrom(NgxPermissionsModule.forRoot()),
    // TranslateDentalPipe used as injectable service across the app
    { provide: TranslateDentalPipe, useClass: TranslateDentalPipe },
    // Capturum packages
    provideCapturumAuth({
      unauthenticatedRejectionRoute: 'auth/login',
      baseUrl: environment.baseUrl,
      production: environment.production,
      authRejectionRoute: 'auth/login',
    }),
    provideCapturumLogin({
      environment,
      productName: 'complete-frontend',
      redirectAfterLogin: 'order',
      loginService: LoginService,
      passwordValidator,
    }),
    provideCapturumShared(),
    provideCapturumApi({
      baseUrl: environment.baseUrl,
      production: environment.production,
      onAuthError,
      dateInterceptor: {
        dateProperties: API_DATE_PROPERTIES,
        blackListedURLs: API_DATE_BLACKLIST,
        blackListedStatusCodes: [401, 204],
        displayFormat: DatesFormats.dateFnsDisplayFormat,
        sendFormat: DatesFormats.dateFnsSendFormat,
      },
    }),
    provideCapturumUI({ primaryColor: '#009ee0' }),
    // Translations
    provideTranslateService({
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CustomMissingTranslationHandler,
      },
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoader,
      },
    }),
    // NGXS State Management
    provideStore(
      [OrderWorkflowState, WorkflowManagerState, FormBuilderState, UsersState],
      withNgxsStoragePlugin({ keys: '*' }),
      withNgxsReduxDevtoolsPlugin({ disabled: environment.production }),
    ),
    // Capturum Builders
    provideCapturumBuildersCore(buildersCoreConfig),
    // Formly
    provideCapturumFormly(),
    provideFormBuilder(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    MessageService,
    DialogService,
    DentalCompleteResolver,
    DentalBaseDataResolver,
    OrderResolver,
    AttributesBaseDataResolver,
    AttributesTranslationResolver,
    FormBuilderResolver,
    FormBuilderApiService,
    {
      provide: FormBuilderConfigService,
      useValue: {
        formBuilderApi: '/form-builder',
        dynamicForms,
        urlPlaceholders,
      },
    },
    // PrimeNG theme
    providePrimeNG({
      theme: {
        preset: appPreset,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    ...(environment.production
      ? [
          {
            provide: ErrorHandler,
            useClass: SentryErrorHandler,
          },
        ]
      : []),
    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCodeInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OrderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CapturumHttpCodeInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorMessageInterceptor,
      multi: true,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    ConfirmationService,
    PermissionGuard,
    provideWorkflowManager({ key: 'order' }),
  ],
};
